import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';

import {
    BlockTypeSelect,
    BoldItalicUnderlineToggles,
    ChangeCodeMirrorLanguage,
    codeBlockPlugin,
    codeMirrorPlugin,
    CodeToggle,
    ConditionalContents,
    CreateLink,
    imagePlugin,
    linkDialogPlugin,
    linkPlugin,
    thematicBreakPlugin,
    headingsPlugin,
    InsertCodeBlock,
    InsertSandpack,
    InsertTable,
    listsPlugin,
    ListsToggle,
    markdownShortcutPlugin,
    MDXEditor,
    MDXEditorMethods,
    quotePlugin,
    ShowSandpackInfo,
    tablePlugin,
    toolbarPlugin,
    UndoRedo
} from '@mdxeditor/editor'
import '@mdxeditor/editor/style.css'

import TextField from '@mui/material/TextField/TextField';
import Typography from '@mui/material/Typography/Typography';
import './Editor.scss';

const Editor = ({
    value,
    toolbar,
    short,
    onChange
}: {
    value: string; // initial value only
    short?: boolean;
    toolbar?: boolean;
    onChange: (value: string) => void;
}) => {
    const ref = useRef<MDXEditorMethods>(null)
    const [initialized, setInitialized] = useState(false);
    useEffect(() => {
        if (ref.current && !initialized) {
            ref.current.setMarkdown(value);
            setInitialized(true);
        }
    }, [value, ref, initialized])
    // parents often pass a new inline callback each render; read the latest one through a ref
    // so the editor's own props stay stable
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    // trailing debounce: report the latest markdown once typing pauses, and on blur/unmount
    // so the final keystrokes are never dropped
    const pending = useRef<string | null>(null);
    const timer = useRef<number | undefined>(undefined);

    const flush = useCallback(() => {
        window.clearTimeout(timer.current);
        if (pending.current !== null) {
            const markdown = pending.current;
            pending.current = null;
            onChangeRef.current(markdown);
        }
    }, []);

    useEffect(() => flush, [flush]);

    // markdown the rich editor can't represent would otherwise be silently dropped on save;
    // fall back to editing the raw text instead
    const [fallbackText, setFallbackText] = useState<string | null>(null);
    const handleError = useCallback(({ error }: { error: string }) => {
        console.warn('Editor could not load this content, switching to plain text:', error);
        setFallbackText(current => current ?? value);
    }, [value]);

    const handleChange = useCallback((markdown: string) => {
        pending.current = markdown;
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(flush, 300);
    }, [flush]);

    const plugins = useMemo(() => {
        const plugins = [
            codeBlockPlugin({ defaultCodeBlockLanguage: 'ts' }),
            codeMirrorPlugin({ codeBlockLanguages: { js: 'JavaScript', css: 'CSS', ts: 'TypeScript', md: 'Markdown', html: 'HTML', py: 'Python', rb: 'Ruby' } }),
            headingsPlugin(),
            // without these, a link (e.g. a meeting link) and everything after it was dropped
            linkPlugin(),
            linkDialogPlugin(),
            imagePlugin(),
            thematicBreakPlugin(),
            listsPlugin(),
            quotePlugin(),
            tablePlugin(),
            markdownShortcutPlugin()
        ]
        if (toolbar) {
            plugins.push(toolbarPlugin({
                toolbarContents: () =>
                    <>
                        <UndoRedo />
                        <BlockTypeSelect />
                        <BoldItalicUnderlineToggles />
                        <CodeToggle />
                        <CreateLink />
                        <InsertTable />
                        <ListsToggle />
                        <ConditionalContents
                            options={[
                                { when: (editor) => editor?.editorType === 'codeblock', contents: () => <ChangeCodeMirrorLanguage /> },
                                { when: (editor) => editor?.editorType === 'sandpack', contents: () => <ShowSandpackInfo /> },
                                {
                                    fallback: () => (<>
                                        <InsertCodeBlock />
                                        <InsertSandpack />
                                    </>)
                                }
                            ]}
                        />
                    </>

            }),)
        }
        return plugins;
    }, [toolbar]);

    return (
        <div className="editor" onBlur={flush}>
            {
                fallbackText !== null ? (
                    <>
                        <TextField
                            multiline
                            fullWidth
                            minRows={short ? 3 : 6}
                            value={fallbackText}
                            onChange={(e) => {
                                setFallbackText(e.target.value);
                                handleChange(e.target.value);
                            }}
                            sx={{ '& fieldset': { border: 'none' } }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, pb: 1 }}>
                            Some formatting can't be shown in the rich editor, so you're editing the Markdown text directly.
                        </Typography>
                    </>
                ) : (
                    <MDXEditor className={short ? 'editor-short' : ''} ref={ref} markdown={value} onChange={handleChange}
                        onError={handleError}
                        plugins={plugins}
                    />
                )
            }
        </div>
    )
}

// memoized: re-renders of a parent form (e.g. typing in another field) skip the heavy MDX editor.
// onChange is read through a ref, so a new callback identity alone is not a reason to re-render.
export default React.memo(Editor, (prev, next) =>
    prev.value === next.value && prev.toolbar === next.toolbar && prev.short === next.short
);

// export const Editor = ({
//     value,
//     onChange
// }: {
//     value: string;
//     onChange: (value: string) => void;
// }) => {

//     const rteRef = useRef<RichTextEditorRef>(null);

//     useEffect(() => {
//         if (rteRef.current && !value) {
//             rteRef.current.editor?.commands.clearContent();
//         }
//     }, [value])

//     return (
//         <RichTextEditor
//             ref={rteRef}
//             extensions={[StarterKit]} // Or any Tiptap extensions you wish!
//             content={value} // Initial content for the editor
//             // Optionally include `renderControls` for a menu-bar atop the editor:
//             renderControls={() => (
//                 <MenuControlsContainer>
//                     <MenuSelectHeading />
//                     <MenuDivider />
//                     <MenuButtonBold />
//                     <MenuButtonItalic />
//                     {/* Add more controls of your choosing here */}
//                     <MenuButtonBulletedList />
//                     <MenuButtonCodeBlock />

//                 </MenuControlsContainer>
//             )}
//         />

//     )
// }